// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { DocumentScope } from '@shared/data-access/types/engine-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { z } from 'zod';

dayjs.extend(customParseFormat);

const FILTER_DATE_FORMAT = 'YYYY-MM-DD';

const optionalFilterDateField = z
  .string()
  .trim()
  .refine(
    value => value === '' || dayjs(value, FILTER_DATE_FORMAT, true).isValid(),
    { message: 'Invalid date' },
  );

const addDateRangeIssues = (
  ctx: z.RefinementCtx,
  fromKey: 'document_date_from' | 'expiry_date_from',
  toKey: 'document_date_to' | 'expiry_date_to',
  from: string,
  to: string,
) => {
  const fromDate = dayjs(from, FILTER_DATE_FORMAT, true);
  const toDate = dayjs(to, FILTER_DATE_FORMAT, true);

  if (
    from &&
    to &&
    fromDate.isValid() &&
    toDate.isValid() &&
    fromDate.startOf('day').isAfter(toDate.startOf('day'))
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Start date must be on or before end date',
      path: [fromKey],
    });
    ctx.addIssue({
      code: 'custom',
      message: 'Start date must be on or before end date',
      path: [toKey],
    });
  }
};

export const documentDrawerFiltersSchema = z
  .object({
    category: z.string().trim(),
    subcategory: z.string().trim(),
    language: z.string().trim(),
    document_date_from: optionalFilterDateField,
    document_date_to: optionalFilterDateField,
    expiry_date_from: optionalFilterDateField,
    expiry_date_to: optionalFilterDateField,
  })
  .superRefine((data, ctx) => {
    addDateRangeIssues(
      ctx,
      'document_date_from',
      'document_date_to',
      data.document_date_from,
      data.document_date_to,
    );
    addDateRangeIssues(
      ctx,
      'expiry_date_from',
      'expiry_date_to',
      data.expiry_date_from,
      data.expiry_date_to,
    );
  });

export type DocumentDrawerFilters = z.infer<typeof documentDrawerFiltersSchema>;

export type DocumentScopeTab = DocumentScope | 'all';

export interface DocumentFiltersState extends DocumentDrawerFilters {
  readonly scope: DocumentScopeTab;
  readonly search: string;
}

export const emptyDocumentDrawerFilters = (): DocumentDrawerFilters => ({
  category: '',
  subcategory: '',
  language: '',
  document_date_from: '',
  document_date_to: '',
  expiry_date_from: '',
  expiry_date_to: '',
});

export const defaultDocumentFiltersState = (): DocumentFiltersState => ({
  scope: 'all',
  search: '',
  ...emptyDocumentDrawerFilters(),
});

export const countActiveDrawerFilters = (filters: DocumentDrawerFilters): number =>
  Object.values(filters).filter(Boolean).length;
